/* Internal dependencies */
import { ParticipantStatus } from '../types/Participant';
import User, { Image } from 'src/types/User';
import { cleanObject } from 'src/common/cleanObject';
import { PaginationOptions, PaginationResponse } from 'src/types/Pagination';
import { modelUser } from 'src/store/helpers/users';
import Community from 'src/types/Community';
import API from '.';
import { listAchievementGroupsForItem } from './achievements';
import { listChartCollectionsForItem } from './charts';
import { fetchAll } from 'src/common/helpers/fetchAll';

export const addCommunity = async (community: any) =>
  (await API.post({
    path: `/communities`,
    options: {
      body: community,
    },
  })) as any as Community;

export const updateCommunity = async (community: any) =>
  (await API.put({
    path: `/communities/${community.id}`,
    options: {
      body: community,
    },
  })) as any as Community;

export const addImageForCommunity = async (communityId: string, image: Omit<Image, 'url'>) =>
  await API.post({
    path: `/communities/${communityId}/images`,
    options: {
      body: image,
    },
  });

export const updateImageForCommunity = async (communityId: string, imageIndex: number, image: Omit<Image, 'url'>) =>
  await API.put({
    path: `/communities/${communityId}/images/${imageIndex}`,
    options: {
      body: image,
    },
  });

export const addCreatorForCommunity = async (communityId: string, { id: creatorId, creatorType }: (User | Community) & { creatorType?: string; }) =>
  await API.post({
    path: `/communities/${communityId}/creators`,
    options: {
      body: cleanObject({ creatorId, creatorType }),
    },
  });

export const removeCreatorForCommunity = async (communityId: string, { id: creatorId }: User | Community) =>
  await API.del({
    path: `/communities/${communityId}/creators/${creatorId}`,
  });

export const addMemberForCommunity = async (communityId: string, memberId: string, status: ParticipantStatus) =>
  await API.post({
    path: `/communities/${communityId}/members/${memberId}`,
    options: {
      body: { status },
    },
  });

export const removeMemberForCommunity = async (communityId: string, memberId: string) =>
  await API.del({
    path: `/communities/${communityId}/members/${memberId}`,
  });

export const listMembersForCommunity = async (
  communityId: string,
  options: PaginationOptions & { status?: ParticipantStatus }
) => {
  const response = await API.get({
    path: `/communities/${communityId}/members`,
    options: {
      queryParams: cleanObject(options),
    },
  }) as any;

  return {
    ...response,
    items: response.items && response.items.map(modelUser),
  };
};

export const listEventsForCommunity = async (
  communityId: string,
  { limit, offset }: PaginationOptions
) => {
  return await API.get({
    path: `/communities/${communityId}/events`,
    options: {
      queryParams: cleanObject({
        limit,
        offset: Boolean(offset) ? JSON.stringify(offset) : undefined,
      }),
    },
  });
};

export const listCreatedCommunitiesForUser = async (
  userId: string,
  { limit, offset }: PaginationOptions
): Promise<PaginationResponse<Community>> => {
  return await API.get({
    path: `/users-communities/${userId}/createdCommunities`,
    options: {
      queryParams: cleanObject({
        limit,
        offset: JSON.stringify(offset),
      }),
    },
  });
};

export const listCommunitiesForUser = async (
  userId: string,
  { limit, offset }: PaginationOptions
): Promise<PaginationResponse<Community>> => {
  return await API.get({
    path: `/users-communities/${userId}/communities`,
    options: {
      queryParams: cleanObject({
        limit,
        offset: JSON.stringify(offset),
      }),
    },
  });
};

export const listUpcomingEventsForCommunity = async (
  communityId: string,
  { limit, offset }: PaginationOptions
) => {
  return await API.get({
    path: `/communities/${communityId}/upcomingEvents`,
    options: {
      queryParams: cleanObject({
        limit,
        offset,
      }),
    },
  });
};

export const listPastEventsForCommunity = async (
  communityId: string,
  { limit, offset }: PaginationOptions
) => {
  return await API.get({
    path: `/communities/${communityId}/pastEvents`,
    options: {
      queryParams: cleanObject({
        limit,
        offset,
      }),
    },
  });
};

export const listRelatedCommunitiesForCommunity = async (communityId: string, options: PaginationOptions = { limit: 5 }) =>
  await API.get({
    path: `/communities/${communityId}/relatedCommunities`,
    options: {
      queryParams: cleanObject(options),
    },
  });

export const listRequestsForCommunity = async (communityId: string, options: PaginationOptions = { limit: 5 }) =>
  await API.get({
    path: `/communities/${communityId}/requests`,
    options: {
      queryParams: cleanObject(options),
    },
  });

export const listAttendeesForCommunity = async (communityId: string, options: PaginationOptions = { limit: 5 }) =>
  await API.get({
    path: `/communities/${communityId}/attendees`,
    options: {
      queryParams: cleanObject(options),
    },
  });

export const listOrdersForCommunity = async (communityId: string, options: PaginationOptions = { limit: 5 }) =>
  await API.get({
    path: `/communities/${communityId}/orders`,
    options: {
      queryParams: cleanObject(options),
    },
  });

export const listArtistsForCommunity = async (communityId: string, options: PaginationOptions = { limit: 5 }) =>
  await API.get({
    path: `/communities/${communityId}/artists`,
    options: {
      queryParams: cleanObject(options),
    },
  });

export const listCreatorsForCommunity = async (communityId: string, options: PaginationOptions = { limit: 5 }) =>
  await API.get({
    path: `/communities/${communityId}/creators`,
    options: {
      queryParams: cleanObject(options),
    },
  });

export const listGenresForCommunity = async (communityId: string, options: PaginationOptions = { limit: 5 }) =>
  await API.get({
    path: `/communities/${communityId}/genres`,
    options: {
      queryParams: cleanObject(options),
    },
  });

export const listHashtagsForCommunity = async (communityId: string, options: PaginationOptions = { limit: 5 }) =>
  await API.get({
    path: `/communities/${communityId}/hashtags`,
    options: {
      queryParams: cleanObject(options),
    },
  });

export const getCommunity = async (communityId: string, options: { cache?: boolean, mini?: boolean; } = { cache: true, mini: false }) =>
  await API.get({
    path: `/communities/${communityId}`,
    options: {
      queryParams: cleanObject(options),
    },
  });

  export const getFullCommunity = async (communityId: string): Promise<Community> => {
    const promises = [
      getCommunity(communityId, { cache: false, mini: true }),
      listEventsForCommunity(communityId, { limit: 8 }),
      listUpcomingEventsForCommunity(communityId, { limit: 10 }),
      listPastEventsForCommunity(communityId, { limit: 3 }),
      listRelatedCommunitiesForCommunity(communityId, { limit: 5 }),
      listMembersForCommunity(communityId, { limit: 5 }),
      listAttendeesForCommunity(communityId, { limit: 5 }),
      listArtistsForCommunity(communityId, { limit: 5 }),
      listCreatorsForCommunity(communityId, { limit: 10 }),
      listGenresForCommunity(communityId, { limit: 5 }),
      listHashtagsForCommunity(communityId, { limit: 5 }),
    ];
  
    const [
      community, events, upcomingEvents, pastEvents, relatedCommunities,
      members, attendees, artists,
      creators, genres, hashtags,
    ] = await fetchAll(promises);
  
    return {
      ...community,
      events,
      upcomingEvents,
      pastEvents,
      relatedCommunities,
      members,
      attendees,
      artists,
      creators,
      genres,
      hashtags,
    };
  };
  
  export const getCurrentFullCommunity = async (communityId: string): Promise<Community> => {
    const promises = [
      getFullCommunity(communityId),
      listRequestsForCommunity(communityId, { limit: 5 }),
      listAchievementGroupsForItem(communityId, { limit: 5 }),
      listChartCollectionsForItem(communityId, { limit: 10 }),
      listOrdersForCommunity(communityId, { limit: 10 }),
    ];
  
    const [
      community, requests, achievementGroups,
      chartCollections, orders,
    ] = await fetchAll(promises);
  
    return {
      ...community,
      requests,
      achievementGroups,
      chartCollections,
      orders,
    };
  };
